import {Dispatch, SetStateAction} from "react";
import {
	LazyMotion,
	domAnimation,
	m,
	MotionProps,
	useReducedMotion,
} from "framer-motion";
import ModalOverlay from "./ModalOverlay";
import {Modal, Dialog, Button, ModalOverlayProps} from "react-aria-components";
import {FocusScope} from "react-aria";
import {FCWithHTMLAttributes} from "@/app/types/reactTypes";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose} from "@fortawesome/sharp-regular-svg-icons/faClose";
import clsMerge from "@/app/util/clsMerge";
import {ModalAnimationState} from "./useModalAnimation";

export type DrawerClasses = {
	dialogRoot?: string;
	closeButton?: string;
};

interface DrawerProps
	extends Omit<ModalOverlayProps & MotionProps, "style" | "children"> {
	anchorSide: AnchorSide;
	animationState: ModalAnimationState;
	classes?: DrawerClasses;
	setAnimation: Dispatch<SetStateAction<ModalAnimationState>>;
	showCloseButton?: boolean;
}

export enum AnchorSide {
	LEFT = "left",
	RIGHT = "right",
	TOP = "top",
	BOTTOM = "bottom",
}

const Drawer: FCWithHTMLAttributes<DrawerProps> = ({
	anchorSide,
	animationState,
	children,
	classes,
	className,
	setAnimation,
	showCloseButton,
	...props
}) => {
	const isHorizontal =
		anchorSide === AnchorSide.LEFT || anchorSide === AnchorSide.RIGHT;
	const isAlignedStart =
		anchorSide === AnchorSide.LEFT || anchorSide === AnchorSide.TOP;

	const shouldReduceMotion = useReducedMotion();

	const drawerAnimationVariants = {
		default: {
			hidden: isHorizontal ? {width: 0} : {height: 0},
			visible: isHorizontal ? {width: "auto"} : {height: "auto"},
		},
		reducedMotion: {
			hidden: {opacity: 0},
			visible: {opacity: 1},
		},
	};

	return (
		<LazyMotion features={domAnimation}>
			<ModalOverlay
				isDismissable
				className={clsMerge(
					"fixed left-0 top-0 z-mobile-menu flex h-screen max-h-dvh w-screen flex-col items-end justify-center bg-gray-900",
					isHorizontal ? "flex-col" : "flex-row",
					isAlignedStart ? "items-start" : "items-end",
					className
				)}
				// Prevent modal from unmounting during animation.
				isExiting={animationState === "hidden"}
				// Reset animation state once it is complete.
				onAnimationComplete={(animation) => {
					// updater function (setup animation hook specifically for the home item.)
					setAnimation((pendingAnimationState: ModalAnimationState) =>
						animation === "hidden" && pendingAnimationState === "hidden"
							? "unmounted"
							: pendingAnimationState
					);
				}}
				variants={{
					hidden: {
						backgroundColor: "rgba(44, 43, 47, 0)",
						backdropFilter: "blur(0px)",
					},
					visible: {
						backgroundColor: "rgba(44, 43, 47, 0.4)",
						backdropFilter: "blur(4px)",
					},
				}}
				initial="hidden"
				animate={animationState}
				{...props}
			>
				<Modal>
					<Dialog
						aria-label="Navigation Menu"
						className={isHorizontal ? "h-dvh" : "w-dvw"}
					>
						{({close}) => {
							return (
								<FocusScope autoFocus contain restoreFocus>
									<m.div
										className={clsMerge(
											"overflow-hidden bg-white",
											isHorizontal
												? "h-full max-w-full"
												: "max-h-screen w-screen",
											classes?.dialogRoot
										)}
										variants={
											shouldReduceMotion
												? drawerAnimationVariants["reducedMotion"]
												: drawerAnimationVariants["default"]
										}
									>
										{children}
										{showCloseButton && animationState === "visible" && (
											<Button
												onPress={close}
												className={clsMerge(
													"absolute right-4 top-3 h-10 w-10 self-end rounded bg-gray-100 text-gray-900 duration-300 hover:bg-gray-300",
													!shouldReduceMotion && "transition-colors",
													classes?.closeButton
												)}
											>
												<FontAwesomeIcon icon={faClose} />
											</Button>
										)}
									</m.div>
								</FocusScope>
							);
						}}
					</Dialog>
				</Modal>
			</ModalOverlay>
		</LazyMotion>
	);
};

export default Drawer;
